<script>
import { defineComponent } from "vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { OrderStopType } from "@/constants/order.ts";
import ValidationMixin from "@/mixins/validation.mixin";
import { OrdersMixin } from "@/views/orders/mixins/orders.mixin.ts";
import TrucksMixin from "@/views/trucks/mixins/TrucksMixin.vue";
import { useDateTime } from "@/composables/index.ts";
import { API } from "@/api/api.js";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store.js";
import TruckRoute from "@/components/truck/TruckRoute.vue";
import { useError } from "@/composables/useError.js";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import { RouteStatus } from "@/constants/trip.ts";
import TruckLink from "@/components/links/TruckLink.vue";

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { showAPIError } = useError();

export default defineComponent({
  components: { TruckLink, TimezoneSelect, TruckRoute, OmniDialog, OmniSelect, DatePicker, TimePicker },
  mixins: [ValidationMixin, OrdersMixin, TrucksMixin],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    stop: Object,
    truckId: Number,
    tripId: Number,
  },
  emits: ["finish", "update:modelValue"],
  data() {
    return {
      loading: false,
      finishData: {},
      truck: null,
      routes: [],
      location: null,
    };
  },
  computed: {
    stopType() {
      switch (this.stop.type) {
        case OrderStopType.pickup:
          return "Pick up";
        case OrderStopType.delivery:
          return "Delivery";
        default:
          return "";
      }
    },
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    timezonesShortListComputed() {
      return timezonesShortList;
    },
    nextPlannedRoute() {
      if (!this.routes.length) {
        return null;
      }

      return this.routes.find((route) => route.status === RouteStatus.planned);
    },
    locationOptions() {
      const options = [];

      if (this.truck.currentLocation) {
        options.push({
          value: this.truck.currentLocation,
          name: this.truck.currentLocation?.fullAddress,
          suffix: "Current location",
        });
      }

      if (this.stop.facility?.parsedAddress) {
        options.push({
          value: {
            ...this.stop.facility.parsedAddress,
            coordinates: {
              lat: this.stop.facility?.addressCoordinates.lat || null,
              lon: this.stop.facility?.addressCoordinates.lon || null,
            },
            zipCode: this.stop.facility.parsedAddress.postCode,
          },
          name: this.stop.facility.parsedAddress.fullAddress,
          suffix: "Facility location",
        });
      }

      return options;
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(value) {
        if (value) {
          await this.loadTruck();
          await this.loadRoutes();
          this.finishData.timezone = this.stop.facility.timezone;
          const date = this.$dayjs().tz(this.finishData.timezone);
          this.finishData.date = date.format("YYYY-MM-DD");
          this.finishData.time = date.format("HH:mm");
        }
      },
    },
    location: {
      handler(value) {
        console.log("value", value);
      },
    },
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError"]),
    async loadTruck() {
      try {
        const response = await api.trucks.findTruckById(this.truckId);
        if (response.data && response.success) {
          this.truck = response.data;
        }
      } catch (error) {
        this.showSnackbarError("Error loading truck");
      }
    },
    async loadRoutes() {
      try {
        const response = await api.trucks.getTruckRoutes(this.truckId);

        if (response.success && response.data) {
          this.routes = response.data.routes;
        } else {
          this.showSnackbarError("Error loading routes");
        }
      } catch (error) {
        console.error(error);
      }
    },
    async finish() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;
      this.loading = true;
      const response = await this.$api.trips.checkOutRouteStop(this.tripId, this.stop.id, {
        checkOutTime: dateToUTC(this.finishData.date + " " + this.finishData.time, this.finishData.timezone),
        truckStatus: this.finishData.truckStatus,
        ...(this.location
          ? {
              address: this.location?.fullAddress || null,
              city: this.location?.city || null,
              state: this.location?.state || null,
              zipCode: this.location?.zipCode || null,
              country: this.location?.country || null,
              lat: this.location?.coordinates?.lat || null,
              lon: this.location?.coordinates?.lon || null,
            }
          : {}),
      });
      if (response.success) {
        this.$emit("finish");
        this.$emit("update:modelValue", false);
      } else {
        showAPIError(response, "Error checking out at stop");
      }
      this.loading = false;
    },
  },
});
</script>

<template>
  <omni-dialog
    v-model="value"
    :disabled="loading"
    :loading="loading"
    :title="'Finish on the ' + stopType"
    primary-action-label="Finish"
    size="large"
    @primary-action:click="finish"
  >
    <v-form ref="form">
      <div class="pb-6 text-grey-darken-1">
        You are about to check out
        <truck-link v-if="truck" :truck-id="truck.id" :truck-number="truck.number" />
        at the {{ stopType }}:
      </div>
      <div class="rounded bg-grey-lighten-4 pa-2 mb-8">
        <router-link
          :to="{ name: 'facilities-edit', params: { id: stop.facility?.id } }"
          class="text-primary font-weight-500 text-decoration-none d-block"
          data-qa="card-facility-name"
          target="_blank"
        >
          {{ stop.facility?.name }}
        </router-link>
        <div class="mt-1 font-size-12 text-grey-darken-2" data-qa="card-facility-address">
          {{ stop.facility?.addressLine }}, {{ stop.facility?.address }}
        </div>
      </div>
      <v-row>
        <v-col cols="4">
          <timezone-select
            v-model="finishData.timezone"
            :items="timezonesShortListComputed"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="5">
          <date-picker v-model="finishData.date" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="finishData.time" label="Time" required></time-picker>
        </v-col>
      </v-row>

      <template v-if="!nextPlannedRoute">
        <div class="font-size-16 align-center font-weight-500 mt-8 mb-6">
          <router-link
            :to="{ name: 'truck-view', params: { id: truckId } }"
            class="text-primary text-decoration-none"
            target="_blank"
          >
            Truck #{{ truck?.number }}
          </router-link>

          <span class="text-dark-grey"> status becomes</span>
        </div>

        <omni-select
          v-model="finishData.truckStatus"
          :items="truckStatuses.filter((s) => s.forTrip).map((s) => ({ ...s, disabled: false }))"
          :rules="[requiredValidator]"
          data-qa="truck-status-select-finish"
          label="Truck status"
          required
        ></omni-select>

        <omni-select
          v-if="finishData.truckStatus === truckStatusAvailable"
          v-model="location"
          :items="locationOptions"
          :rules="[requiredValidator]"
          class="mt-6"
          data-qa="truck-location-select-finish"
          item-value="value"
          label="Location"
          required
        ></omni-select>
      </template>

      <div v-else class="mt-6">
        <div class="font-size-16 font-weight-500 text-grey-darken-2">
          After completing this route

          <router-link
            :to="{ name: 'truck-view', params: { id: truckId } }"
            class="text-primary text-decoration-none"
            target="_blank"
          >
            Truck #{{ truck?.number }}
          </router-link>

          will proceed to the next planned route:
        </div>

        <div class="bg-grey-lighten-4 pa-4 mt-5">
          <truck-route :route="nextPlannedRoute"></truck-route>
        </div>
      </div>
    </v-form>
  </omni-dialog>
</template>
